import { runInAction } from 'mobx';

const INSTALL_NEVER_KEY     = 'ch_pwa_install_never_at';
const PWA_LOG_KEY_PREFIX    = 'ch_pwa_log_sent_';

/**
 * Registra il listener `beforeinstallprompt` e, se le condizioni sono soddisfatte,
 * mostra il banner di installazione dopo il delay configurato.
 * Deve essere chiamata dopo il render iniziale (main.jsx).
 */
export const pwaInstallInit = function () {
    const cfg = this.config?.pwaInstallPrompt;
    if (!cfg?.enabled) {
        runInAction(() => { this.pwaInstallBannerVisible = false; });
        return;
    }

    // disable PWA install banner if route is unprotected (such as "segnalazione danni", "tracking order", etc)
    if (this?.routeParamsCurrent?.unproctected) {
        runInAction(() => { this.pwaInstallBannerVisible = false; });
        return;
    }

    // Controlla il cooldown dopo un "non chiedermelo più" perchè ad un certo momento, l'utente si dimenticherà di averlo non accettato :)
    const installNeverAt = localStorage.getItem(INSTALL_NEVER_KEY);
    if (installNeverAt) {
        const cooldownDays = cfg.dismissCooldownDays ?? 30;
        const daysSince = (Date.now() - parseInt(installNeverAt, 10)) / 86400000;
        if (daysSince < cooldownDays) {
            runInAction(() => { this.pwaInstallBannerVisible = false; });
            return;
        }
    }

    // ── iOS / Safari ────────────────────────────────────────────────────────
    // Safari non implementa beforeinstallprompt. Mostriamo un banner con
    // le istruzioni manuali (Condividi → Aggiungi a schermata Home).
    const isIos        = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                         || window.navigator.standalone === true;

    if (isStandalone) {
        runInAction(() => { this.pwaInstallBannerVisible = false; });
        return;
    }

    if (isIos) {
        runInAction(() => {
            this.pwaInstallIsIos         = true;
            this.pwaInstallBannerVisible = true;
        });
        return;
    }

    // ── Safari macOS ─────────────────────────────────────────────────────────
    // Safari su macOS non emette beforeinstallprompt. Safari 17.4+ permette
    // l'installazione tramite il pulsante "+" nella barra degli indirizzi o
    // File → Aggiungi al Dock.
    const isSafariMac = /^((?!chrome|android|fxios|crios).)*safari/i.test(navigator.userAgent)
                        && /macintosh/i.test(navigator.userAgent);

    if (isSafariMac) {
        runInAction(() => {
            this.pwaInstallIsSafariMac   = true;
            this.pwaInstallBannerVisible = true;
        });
        return;
    }

    // ── Chrome / Edge / altri browser ────────────────────────────────────────
    const applyInstallEvent = (e) => {
        runInAction(() => {
            this.pwaInstallDeferredEvent = e;
            this.pwaInstallBannerVisible = true;
        });
    };

    // L'evento può essere già arrivato prima dell'init (catturato in main.jsx)
    if (window._pwaBeforeInstallPromptEvent) {
        applyInstallEvent(window._pwaBeforeInstallPromptEvent);
        window._pwaBeforeInstallPromptEvent = null;
    } else {
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            applyInstallEvent(e);
        });
    }
};

/**
 * Invoca il prompt nativo del browser per l'installazione della PWA.
 */
export const pwaInstallTrigger = async function () {
    const e = this.pwaInstallDeferredEvent;
    if (!e) return;

    e.prompt();
    const { outcome } = await e.userChoice;

    runInAction(() => {
        this.pwaInstallBannerVisible = false;
        this.pwaInstallDeferredEvent = null;
    });

    if (outcome === 'accepted') {
        // Pulizia: rimuove il cooldown se l'utente ha installato
        localStorage.removeItem(INSTALL_NEVER_KEY);
    }
};

/**
 * L'utente ha scelto "X" di chiusura toast banner
 */
export const pwaInstallClose = function () {
    runInAction(() => {
        this.pwaInstallBannerVisible = false;
    });
};

/**
 * Invia al backend il log di prima apertura in modalità PWA standalone.
 * Eseguita una sola volta per utente (flag in localStorage).
 */
export const pwaInstallLog = async function () {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                         || window.navigator.standalone === true;
    if (!isStandalone) return;

    const cUtente = this.userInfo?.c_utente;
    if (!cUtente) return;

    const storageKey = `${PWA_LOG_KEY_PREFIX}${cUtente}`;
    if (localStorage.getItem(storageKey)) return;

    try {
        await this.dataLayer({
            url: this.config.paths.apiURL + 'log/pwa-installed',
            params: {
                c_utente: cUtente,
                tipo_azione: 'INSTALLAZIONE_PWA',
                user_agent: navigator.userAgent,
                dt_ins: new Date().toISOString(),
            },
            userToken: this.userToken,
            method: 'POST',
            rawResponse: true,
        });
        localStorage.setItem(storageKey, '1');
    } catch (e) {
        this.log(e, 'WARNING pwaInstallLog');
    }
};

/**
 * L'utente ha scelto "Non mostrare più": flag permanente in localStorage.
 */
export const pwaInstallNever = function () {
    localStorage.setItem(INSTALL_NEVER_KEY, Date.now().toString());
    runInAction(() => {
        this.pwaInstallBannerVisible = false;
    });
};

/**
 * Confronta il BUILD_ID compilato nel bundle con version.txt sul server
 * (fetch no-store: mai dalla cache HTTP). Se differiscono, il bundle in
 * esecuzione è stantio (index.html cachato o resume da memoria su PWA iOS):
 * pulizia completa di Cache Storage + service worker e reload forzato.
 * Guard anti-loop: un solo tentativo di reload per versione attesa.
 */
export const buildVersionCheck = async function () {
    if (typeof __BUILD_ID__ === 'undefined') return;

    try {
        let res = await fetch('./version.txt', { cache: 'no-store' });
        if (!res.ok) {
            res = await fetch(window.location.origin + '/version.txt', { cache: 'no-store' });
        }
        if (!res.ok) return;

        const expected = (await res.text()).trim();
        // contenuto anomalo (es. index.html da rewrite SPA) → ignora
        if (!expected || expected.length > 100 || expected.includes('<')) return;
        if (expected === __BUILD_ID__) return;

        const RELOAD_KEY = 'ch_build_reload_attempted';
        if (sessionStorage.getItem(RELOAD_KEY) === expected) return;
        sessionStorage.setItem(RELOAD_KEY, expected);

        this.log(`Bundle stantio: in esecuzione ${__BUILD_ID__}, atteso ${expected}. Reload forzato`, 'WARNING');

        if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
        if (navigator.serviceWorker) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
        }
        window.location.reload();
    } catch (_) {
        // offline o server irraggiungibile: nessuna azione, si riprova al prossimo avvio/foreground
    }
};
